import {
  ChangeEvent,
  type ElementType,
  KeyboardEvent,
  type MutableRefObject,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box } from '@mui/material';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { flushSync } from 'react-dom';
import MaskedInput, { type MaskedInputProps } from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItemLeadingAction,
  PrimaryNavigationMenuSearch,
  SecondaryNavigationSelector,
  Tooltip,
} from 'components/ui';
import { useZoom } from 'context/ZoomContext';
import { gridShortDate, isValidDate, TEXT_MASK_DATE_FORMAT } from 'utilities';
import { FinancialsDateSelectorProps } from './FinancialsDateSelector.props';

const FINANCIALS_DATE_DISABLED_TOOLTIP = 'Changing the Financials Date is disabled while these financials are locked.';

const HiddenField = () => <div />;

interface FinancialsDateMaskInputProps extends Omit<MaskedInputProps, 'mask'> {
  inputRef: Ref<HTMLInputElement>;
}

const FinancialsDateMaskInput = ({ inputRef, ...props }: FinancialsDateMaskInputProps) => {
  const setInputRef = (value: HTMLInputElement | null) => {
    if (typeof inputRef === 'function') {
      inputRef(value);
      return;
    }

    if (inputRef) {
      const mutableInputRef = inputRef as MutableRefObject<HTMLInputElement | null>;
      mutableInputRef.current = value;
    }
  };

  const handleRef = (ref: MaskedInput | null) => {
    setInputRef(ref ? ref.inputElement as HTMLInputElement : null);
  };

  return (
    <MaskedInput
      {...props}
      ref={handleRef}
      guide
      keepCharPositions={false}
      mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
      placeholderChar="_"
      showMask
      pipe={createAutoCorrectedDatePipe(TEXT_MASK_DATE_FORMAT, {
        minYear: 1900,
      })}
    />
  );
};

const FinancialsDateMaskInputComponent = FinancialsDateMaskInput as ElementType<InputBaseComponentProps>;

const FinancialsDateSelectorPopover = ({ onDateChange, currentDate, disabled = false }: FinancialsDateSelectorProps) => {
  const { zoomLevel } = useZoom();
  const [inputValue, setInputValue] = useState(() => (currentDate ? gridShortDate(currentDate) : ''));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isCalendarActionHovered, setIsCalendarActionHovered] = useState(false);
  const [positiveFeedbackKey, setPositiveFeedbackKey] = useState(0);
  const chipRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Opening the calendar unmounts the menu input; skip that blur so it does not close the picker.
  const skipBlurCommitRef = useRef(false);
  const skipBlurResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInputValue(currentDate ? gridShortDate(currentDate) : '');
  }, [currentDate]);

  // Clear the calendar-open blur guard if the selector unmounts before the reset tick.
  useEffect(() => () => {
    if (skipBlurResetTimerRef.current) {
      clearTimeout(skipBlurResetTimerRef.current);
    }
  }, []);

  const handleMenuClose = useCallback(() => {
    setPopoverOpen(false);
    setHasError(false);
  }, []);

  const handleClose = useCallback(() => {
    handleMenuClose();
    setPickerOpen(false);
  }, [handleMenuClose]);

  const commitDate = useCallback(
    (text: string) => {
      if (!text.trim()) {
        setHasError(false);
        handleClose();
        return;
      }
      if (isValidDate(text)) {
        const formatted = gridShortDate(text);
        const currentFormattedDate = currentDate ? gridShortDate(currentDate) : '';

        flushSync(() => {
          setInputValue(formatted);
          setHasError(false);
          handleClose();
        });

        if (formatted === currentFormattedDate) {
          return;
        }

        setPositiveFeedbackKey(key => key + 1);
        onDateChange(formatted);
      } else {
        setHasError(true);
      }
    },
    [currentDate, onDateChange, handleClose]
  );

  const handleOpen = useCallback(() => {
    if (disabled) {
      return;
    }
    // Sync input to the latest committed date when opening.
    setInputValue(currentDate ? gridShortDate(currentDate) : '');
    setHasError(false);
    setPopoverOpen(true);
  }, [disabled, currentDate]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (hasError) {
      setHasError(false);
    }
  }, [hasError]);

  const handleInputKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDate(inputValue);
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [commitDate, handleClose, inputValue]);

  const handleBlur = useCallback(() => {
    if (skipBlurCommitRef.current) {
      return;
    }

    commitDate(inputValue);
  }, [commitDate, inputValue]);

  const handleCalendarOpen = useCallback(() => {
    skipBlurCommitRef.current = true;
    handleMenuClose();
    setPickerOpen(true);

    if (skipBlurResetTimerRef.current) {
      clearTimeout(skipBlurResetTimerRef.current);
    }
    skipBlurResetTimerRef.current = setTimeout(() => {
      skipBlurCommitRef.current = false;
    }, 0);
  }, [handleMenuClose]);

  const handleCalendarActionKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCalendarOpen();
    }
  }, [handleCalendarOpen]);

  const handlePickerAccept = useCallback(
    (date: Date | null) => {
      if (date) {
        const formatted = gridShortDate(date);
        const currentFormattedDate = currentDate ? gridShortDate(currentDate) : '';

        flushSync(() => {
          setInputValue(formatted);
          setHasError(false);
          handleClose();
        });

        if (formatted === currentFormattedDate) {
          setPickerOpen(false);
          return;
        }

        setPositiveFeedbackKey(key => key + 1);
        onDateChange(formatted);
      }
      setPickerOpen(false);
    },
    [currentDate, onDateChange, handleClose]
  );

  const chipValue = currentDate ? gridShortDate(currentDate) : '—';
  const dateValue = inputValue ? moment(inputValue, 'MM/DD/YYYY').toDate() : null;

  return (
    <>
      <Tooltip title={disabled ? FINANCIALS_DATE_DISABLED_TOOLTIP : ''}>
        <Box
          ref={chipRef}
          component="span"
          style={{
            cursor: disabled ? 'not-allowed' : undefined,
            display: 'inline-flex',
          }}>
          <SecondaryNavigationSelector
            aria-expanded={popoverOpen}
            aria-haspopup="dialog"
            disabled={disabled}
            label="Financials Date"
            onClick={handleOpen}
            positiveFeedbackKey={positiveFeedbackKey}
            value={chipValue}
          />
        </Box>
      </Tooltip>

      <PrimaryNavigationMenu
        open={popoverOpen}
        anchorEl={chipRef.current}
        onClose={handleMenuClose}
        disableAutoFocusItem
        horizontalPlacement="right"
        menuMaxWidth={220}
        menuMinWidth={156}
        menuWidth="content"
        zoomLevel={zoomLevel}
        TransitionProps={{ onEntered: () => inputRef.current?.focus() }}>
        <PrimaryNavigationMenuSearch
          inputComponent={FinancialsDateMaskInputComponent}
          inputProps={{ inputRef }}
          onBlur={handleBlur}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder="MM/DD/YYYY"
          startAdornment={false}
          type="text"
          value={inputValue}
          endAdornment={(
            <PrimaryNavigationMenuItemLeadingAction
              aria-label="Open financials date picker"
              isVisible
              onClick={handleCalendarOpen}
              onMouseDown={event => event.preventDefault()}
              onMouseEnter={() => setIsCalendarActionHovered(true)}
              onMouseLeave={() => setIsCalendarActionHovered(false)}
              onKeyDown={handleCalendarActionKeyDown}
              role="button"
              tabIndex={0}
              style={{
                backgroundColor: isCalendarActionHovered ? 'rgba(0,0,0,0.1)' : undefined,
                borderRadius: '6px',
                height: 20,
                padding: 2,
                transition: 'background-color 120ms ease',
                width: 20,
              }}>
              <EditOutlinedIcon fontSize="inherit" />
            </PrimaryNavigationMenuItemLeadingAction>
          )}
          sx={{
            mb: 0,
            minWidth: 154,
            ...(hasError
              ? {
                borderColor: 'error.main',
                color: 'error.main',
                '& .MuiInputBase-input': {
                  color: 'error.main',
                },
                '&.Mui-focused': {
                  borderColor: 'error.main',
                },
              }
              : {}),
            '&.Mui-focused': {
              borderColor: hasError ? 'error.main' : 'primary.main',
            },
          }}
        />
      </PrimaryNavigationMenu>

      <DatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={dateValue}
        onAccept={handlePickerAccept}
        openTo="day"
        views={['year', 'month', 'day']}
        slots={{ field: HiddenField }}
        slotProps={{ popper: { anchorEl: chipRef.current } }}
      />
    </>
  );
};

export default FinancialsDateSelectorPopover;
